import { useState } from 'react';
import { useAppDispatch, useAppSelector } from './useRedux';
import { useLocation } from 'react-router-dom';

import { selectMenu } from '../features/infoSlice';
import { selectStorage } from '../features/storageSlice';

import { useValidateDuplicationMutation } from '../services/drive';

import useModal from './useModal';
import { uppyLibrary, uppyDrive } from '../components/Upload';

import { ValidatedResource } from '../types/drive';
import { clearCredentials } from '../features/authSlice';

import { convertingSupportList } from '../utils/util';

export default function useUpload() {
  const dispatch = useAppDispatch();
  const menu = useAppSelector(selectMenu);
  const storage = useAppSelector(selectStorage);
  const { toggleModal } = useModal();
  const [validateDuplication] = useValidateDuplicationMutation();

  const location = useLocation();
  const folderId = location?.state?.folderId;

  const [uploadingSize, setUploadingSize] = useState(0);

  const upload = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: string,
    files: any
  ) => {
    const mainSection =
      menu === 'main' ? (id.includes('library') ? 'library' : 'mydrive') : '';
    const uppy =
      mainSection === 'library' || menu === 'library' ? uppyLibrary : uppyDrive;
    const fileList: File[] = Array.from(files);
    const totalSize = fileList.reduce((acc, file) => acc + file.size, 0);

    let isStopUpload = false;

    const openUploadModal = () => {
      const name = `upload-${menu === 'main' ? mainSection : menu}`;
      toggleModal({
        name,
        id: `${name}-progress`,
        type: 'upload',
        isOpen: true,
      });
      e.target.value = '';
    };

    if (!files?.length || id === 'updateCover') return;

    fileList.some(({ name, size, type }) => {
      const ext = name.split('.').pop()?.toLocaleLowerCase() ?? '';
      const isVideo = type.split('/')[0] === 'video';

      if (menu === 'library' && !convertingSupportList.includes(ext)) {
        toggleModal({ id: 'confirmFileExt', type: 'confirm' });
        isStopUpload = true;
        return true;
      } else if ((!isVideo && size > storage.file) || size > storage.video) {
        toggleModal({ id: 'confirmFileSize', type: 'confirm' });
        isStopUpload = true;
        return true;
      } else if (
        storage &&
        (totalSize + storage.used >= storage.limit ||
          totalSize + storage.used + uploadingSize >= storage.limit)
      ) {
        toggleModal({ id: 'confirmBuyStorage', type: 'confirm' });
        isStopUpload = true;
        return true;
      }
    });

    if (isStopUpload) {
      e.target.value = '';
      return;
    }
    setUploadingSize(totalSize);

    if (mainSection === 'mydrive' || menu === 'mydrive') {
      const resourceList = fileList.reduce(
        (acc: any, file: any) => [...acc, { orgFileName: file.name }],
        []
      );
      try {
        validateDuplication({
          folderId,
          resourceList,
        })
          .unwrap()
          .then((res) => {
            const existingFilesLen = res.data.resourceList.reduce(
              (acc: number, resource: ValidatedResource) =>
                acc + resource.existYn,
              0
            );
            if (existingFilesLen) {
              toggleModal({
                id: 'confirmFileReplace',
                type: 'confirm',
                data: {
                  type: 'file',
                  length: existingFilesLen,
                  handler: (data: any) => {
                    uppy.setMeta(data);
                    uppy.addFiles(files);
                    openUploadModal();
                  },
                },
                isOpen: true,
              });
            } else {
              uppy.setMeta({ overwriteYn: 1, folderId });
              uppy.addFiles(files);
              openUploadModal();
            }
          });
      } catch (error) {
        console.error('Failed to validate files', error);
      }
    }

    if ((!isStopUpload && mainSection === 'library') || menu === 'library') {
      uppy.addFiles(files);
      openUploadModal();
    }
  };

  return { upload };
}
